import React, { useEffect, useState } from 'react'
import styles from './deviceSettings__monitorSettings.jcss'
import translate from '../../../core/translate'
import Dropdown from 'blocks.simple/dropdown/dropdown'
import Icon from '../../../blocks.simple/icon/icon'
import EditText from '../../../blocks.simple/editText/editText'
import { Button } from '../../../molecules/Button'
import { Radio } from '../../../atoms/Radio'
import grid from '../../../blocks.simple/grid/grid.jcss'
import { api } from '../../../core/api/ConnectionManager'
import { cn } from 'ethcss'
import { palette, text as textStyles } from '../../../theme'
import { Typography } from '../../../atoms/Typography'

type Screen = {
    id: number
    name: string
    order: number
    resolution: {
        width: number
        height: number
    }
    enabled: boolean
    orientation: string
    scale: number
    useStretchMode: boolean
}

export interface IMonitorSettings {
    osViewType: string
    appPosition: string
    masterScreenId: number
    appByPixelSettings: {
        left: number
        top: number
        width: number
        height: number
    }
    screens: Screen[]
}

export const MonitorSettings = ({
    monitorSettings,
    onChange,
    enabled,
    deviceId,
    online,
}: {
    monitorSettings: IMonitorSettings
    enabled: boolean
    onChange: (monitorSettings: IMonitorSettings, field: string) => void
    deviceId: number
    online: boolean
}) => {
    const [editedScreen, setEditedScreen] = useState<Screen | null>(null)
    const orientationsOptions = [
        {
            id: 'horizontal',
            name: translate('horizontal'),
        },
        {
            id: 'vertical',
            name: translate('vertical'),
        },
    ]
    const scaleOptions = [
        {
            id: 100,
            name: '100',
        },
        {
            id: 125,
            name: '125',
        },
        {
            id: 150,
            name: '150',
        },
        {
            id: 175,
            name: '175',
        },
    ]

    const getMasterScreenValue = () => {
        const masterScreen = monitorSettings.screens.find((screen) => screen.id === monitorSettings.masterScreenId)

        if (masterScreen) {
            return masterScreen.id
        }

        return null
    }

    const getMasterScreenOptions = () => {
        return monitorSettings.screens.map((screen) => {
            return {
                id: screen.id,
                name: screen.name,
            }
        })
    }

    const onChangeMasterScreen = (masterScreenId: number) => {
        const data = {
            ...monitorSettings,
            masterScreenId,
        }

        onChange(data, 'monitorSettings')
    }

    const onChangeAppPosition = (appPosition: string) => {
        const data = {
            ...monitorSettings,
            appPosition,
        }

        if (appPosition === 'stretch') {
            data.screens.forEach((screen) => {
                screen.useStretchMode = true
            })
        }
        if (appPosition === 'stretchOne' && data.screens.length) {
            data.screens.forEach((screen, index) => {
                screen.useStretchMode = index === 0
            })
        }

        onChange(data, 'monitorSettings')
    }

    const onChangeAppByPixelSettings = (key: string, value: string) => {
        const data = {
            ...monitorSettings,
            appByPixelSettings: {
                ...monitorSettings.appByPixelSettings,
                [key]: +value.replace(/[^\d.]/g, ''),
            },
        }

        onChange(data, 'monitorSettings')
    }

    const onApplyEditScreen = () => {
        if (!editedScreen) return

        const screenData = { ...editedScreen }

        if (screenData.scale < 100) {
            screenData.scale = 100
        }

        if (screenData.scale > 500) {
            screenData.scale = 500
        }

        const data = {
            ...monitorSettings,
            screens: monitorSettings.screens.map((screen) => {
                if (screen.id === screenData.id) {
                    return screenData
                }

                return screen
            }),
        }

        onChange(data, 'monitorSettings')

        onCancelEditScreen()
    }

    const onCheckForStretchMode = (id: number) => {
        const data = {
            ...monitorSettings,
            screens: monitorSettings.screens.map((screen) => {
                return {
                    ...screen,
                    useStretchMode: screen.id === id,
                }
            }),
        }

        onChange(data, 'monitorSettings')
    }

    const onCancelEditScreen = () => {
        setEditedScreen(null)
    }

    const onActualyzeData = () => {
        api.send('getMonitorSettings', { digitalSignageId: deviceId })
    }

    const monitorDataReceived = (data: any) => {
        if (data) {
            onChange(data, 'monitorSettings')
        }
    }

    useEffect(() => {
        const listenersId: string[] = []

        api.addObserver('monitorSettingsReceived', monitorDataReceived, listenersId)

        return () => {
            listenersId.forEach((id) => api.removeObserver(id))
        }
    })

    return enabled && monitorSettings ? (
        <div className={styles.monitorSettings}>
            <div className={styles.monitorsMode}>
                <Typography className={cn(textStyles.normal, grid.mb_md, styles.monitorsModeTitle)} type="title">
                    {translate('displaysWorkingType')}
                </Typography>
                <div className={styles.osType}>{translate(monitorSettings.osViewType)}</div>
            </div>

            <div className={styles.monitorsControl}>
                <div className={styles.monitorsControlTitle}>{translate('monitorsControl')}</div>
                <div className={styles.hint}>{translate('screenGrid')}</div>
                <div className={styles.monitorsControlScreenList}>
                    {monitorSettings.screens.map((screen) => (
                        <div
                            className={`
                        ${styles.screen} 
                        ${!screen.enabled ? styles.disabledScreen : ''} 
                        ${styles[screen.orientation]}
                        `}
                        >
                            {screen.id === monitorSettings.masterScreenId && <div className={styles.screenMaster} />}
                            <div className={styles.screenNumber}>{screen.order + 1}</div>
                        </div>
                    ))}
                </div>
                <div className={styles.masterScreenDropdown}>
                    <Dropdown
                        value={getMasterScreenValue()}
                        label={translate('primaryMonitor')}
                        options={getMasterScreenOptions()}
                        onChange={(selected: { id: number; name: string }) => {
                            onChangeMasterScreen(selected.id)
                        }}
                    />
                </div>
                <div className={styles.screenListWrapper}>
                    <div className={styles.screenListCaption}>{translate('monitorName')}</div>
                    <div className={styles.screenList}>
                        {monitorSettings.screens.map((screen) => (
                            <div className={styles.screenItem}>
                                <div className={styles.screenItemNumber}>{screen.order + 1}</div>
                                <div className={styles.screenItemName}>{screen.name}</div>
                                <div
                                    className={styles.screenItemActionButton}
                                    onClick={() => {
                                        setEditedScreen(screen)
                                    }}
                                >
                                    <Icon
                                        type={'treeDots'}
                                        tooltip={{ title: translate('screenParameters') }}
                                        size={20}
                                    />
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
                <div className={styles.appViewType}>
                    <div>{translate('appPositionInScreens')}</div>
                    <div className={styles.appViewTypeRadio}>
                        <Radio
                            label={translate('stretchOnAllScreens')}
                            selected={monitorSettings.appPosition === 'stretch'}
                            className={grid.mr_md}
                            onClick={() => {
                                onChangeAppPosition('stretch')
                            }}
                        />
                        <Radio
                            label={translate('stretchOnOneScreens')}
                            selected={monitorSettings.appPosition === 'stretchOne'}
                            className={grid.mr_md}
                            onClick={() => {
                                onChangeAppPosition('stretchOne')
                            }}
                        />
                        <Radio
                            label={translate('byPixels')}
                            selected={monitorSettings.appPosition === 'byPixel'}
                            onClick={() => {
                                onChangeAppPosition('byPixel')
                            }}
                        />
                    </div>
                    {monitorSettings.appPosition === 'byPixel' && (
                        <div className={styles.byPixelSettings}>
                            <div className={styles.byPixelSettingsRow}>
                                <EditText
                                    label={translate('topIndent')}
                                    value={monitorSettings.appByPixelSettings.top}
                                    onChange={(value: string) => {
                                        onChangeAppByPixelSettings('top', value)
                                    }}
                                    containerClass={styles.byPixelInput}
                                />
                                <EditText
                                    label={translate('leftIndent')}
                                    value={monitorSettings.appByPixelSettings.left}
                                    onChange={(value: string) => {
                                        onChangeAppByPixelSettings('left', value)
                                    }}
                                    containerClass={styles.byPixelInput}
                                />
                            </div>
                            <div className={styles.byPixelSettingsRow}>
                                <EditText
                                    label={translate('width')}
                                    value={monitorSettings.appByPixelSettings.width}
                                    onChange={(value: string) => {
                                        onChangeAppByPixelSettings('width', value)
                                    }}
                                    containerClass={styles.byPixelInput}
                                />
                                <EditText
                                    label={translate('height')}
                                    value={monitorSettings.appByPixelSettings.height}
                                    onChange={(value: string) => {
                                        onChangeAppByPixelSettings('height', value)
                                    }}
                                    containerClass={styles.byPixelInput}
                                />
                            </div>
                        </div>
                    )}
                    {monitorSettings.appPosition === 'stretchOne' && (
                        <div>
                            <div className={styles.monitorsControlScreenList}>
                                {monitorSettings.screens.map((screen) => (
                                    <div
                                        key={screen.id}
                                        className={`${styles.screen} ${!screen.enabled ? styles.disabledScreen : ''}`}
                                    >
                                        <Radio
                                            className={styles.radio}
                                            onClick={() => {
                                                onCheckForStretchMode(screen.id)
                                            }}
                                            selected={screen.useStretchMode}
                                        />
                                        <div className={styles.screenNumber}>{screen.order + 1}</div>
                                    </div>
                                ))}
                            </div>
                            <div className={cn(styles.tooltip, styles.hint)}>{translate('selectOneScreenForView')}</div>
                        </div>
                    )}
                    <div className={styles.actualization}>
                        <div className={styles.actualizationTitle}>{translate('actualizeData')}</div>
                        <Button onClick={onActualyzeData} mod={'fillBrand'}>
                            {translate('actualize')}
                        </Button>
                    </div>
                </div>
            </div>
            {editedScreen && (
                <div className={styles.screenModal}>
                    <div className={styles.screenModalInner}>
                        {/*<div className={styles.screenModalCaptionWrapper}>*/}
                        {/*    <div className={styles.screenModalCaption}>{editedScreen.name}</div >*/}
                        {/*    <SwitchButton*/}
                        {/*        checked={editedScreen.enabled}*/}
                        {/*        onChange={() => {*/}
                        {/*            setEditedScreen({*/}
                        {/*                ...editedScreen,*/}
                        {/*                enabled: !editedScreen.enabled,*/}
                        {/*            })*/}
                        {/*        }}*/}
                        {/*    />*/}
                        {/*</div>*/}
                        <div>
                            <div className={styles.screenModalRow}>
                                <div className={styles.screenModalCol}>
                                    <div>{translate('scale')}</div>
                                    {/*<div>{translate('scaleInPercentRange')}</div>*/}
                                </div>
                                <div className={styles.screenModalCol}>
                                    <Dropdown
                                        value={editedScreen.scale}
                                        options={scaleOptions}
                                        containerClassName={styles.dropdownWrapper}
                                        onChange={(selected: { id: number; name: string }) => {
                                            setEditedScreen({
                                                ...editedScreen,
                                                scale: selected.id,
                                            })
                                        }}
                                    />
                                    {/*<EditText*/}
                                    {/*    value={editedScreen.scale}*/}
                                    {/*    onChange={(value: string) => {*/}
                                    {/*        setEditedScreen({*/}
                                    {/*            ...editedScreen,*/}
                                    {/*            scale: +value.replace(/[^\d.]/g, ''),*/}
                                    {/*        })*/}
                                    {/*    }}*/}
                                    {/*/>*/}
                                </div>
                            </div>
                            <div className={styles.screenModalRow}>
                                <div className={styles.screenModalCol}>
                                    <div>{translate('resolution')}</div>
                                </div>
                                <div className={styles.screenModalCol}>
                                    {editedScreen.resolution.width}x{editedScreen.resolution.height}
                                </div>
                            </div>
                            <div className={`${styles.screenModalRow} ${styles.centering}`}>
                                <div className={styles.screenModalCol}>{translate('orientation')}</div>
                                <div className={styles.screenModalCol}>
                                    <Dropdown
                                        value={editedScreen.orientation}
                                        options={orientationsOptions}
                                        containerClassName={styles.dropdownWrapper}
                                        onChange={(selected: { id: string; name: string }) => {
                                            setEditedScreen({
                                                ...editedScreen,
                                                orientation: selected.id,
                                            })
                                        }}
                                    />
                                </div>
                            </div>
                        </div>
                        <div className={styles.buttons}>
                            <Button mod={'withBorder'} className={styles.button} onClick={onCancelEditScreen}>
                                {translate('cancel')}
                            </Button>
                            <Button mod={'fill'} className={styles.button} onClick={onApplyEditScreen}>
                                {translate('apply')}
                            </Button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    ) : null
}
